import React, { useMemo, useState } from "react";

import { alpha, Box, Grid, lighten, Stack, Typography, useTheme } from "@mui/material";

import constants from "../../assets/constants";

const OmniChatBubble = React.memo(({ msg, highlightId, handleMessageClick, readOnly = false }) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const isUser = msg.direction === "incoming";
  const isHighlighted = highlightId === msg.messageId;
  const isBotAndHasFeedback = !isUser && msg.hasFeedback;

  // In read-only mode the bubble is purely a transcript rendering: no click to
  // edit/add feedback, no hover affordances, no feedback panel.
  const interactive = !readOnly && !isUser;

  // Show the feedback panel only when it exists and the user is hovering
  const showFeedback = !readOnly && isBotAndHasFeedback && isHovered;

  const borderRadius = isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px";

  const timeStamp = useMemo(() => {
    const rawDate = new Date(msg.date).toLocaleString();
    return isUser ? `${rawDate} : USER` : `BOT : ${rawDate}`;
  }, [msg.date, isUser]);

  const handleClick = e => {
    if (!interactive) return;
    e.stopPropagation();
    handleMessageClick?.(msg);
  };

  return (
    <Grid
      size={{ xs: 12 }}
      container
      flexDirection={"column"}
      sx={{
        marginBottom: theme.spacing(0.5),
        position: "relative",
        // Elevate the entire grid row when hovering so the
        // absolute feedback panel renders ABOVE the following messages.
        zIndex: isHovered ? 1000 : 1,
      }}
    >
      <Stack
        onClick={interactive ? handleClick : null}
        onMouseEnter={interactive ? () => setIsHovered(true) : null}
        onMouseLeave={interactive ? () => setIsHovered(false) : null}
        sx={[
          {
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
            position: "relative",
            width: isUser ? "100%" : "calc(90% + 24px)",
            padding: theme.spacing(1),
            paddingTop: theme.spacing(0.25),
            borderRadius: borderRadius,
            border: "1px solid transparent",
            transition:
              "background-color 0.2s ease, border-color 0.2s ease, border-radius 0.2s ease, box-shadow 0.2s ease",
            userSelect: "none",
          },
          interactive &&
            msg.hasFeedback && {
              backgroundColor: lighten(theme.palette.commonground.contrastText, 0.85),
              borderColor: lighten(theme.palette.commonground.contrastText, 0.5),
              boxShadow: isHighlighted ? theme.shadows[4] : theme.shadows[1],
              cursor: "pointer",
              ...(showFeedback && {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderBottomColor: "transparent",
              }),
            },
          interactive &&
            isHovered && {
              backgroundColor: lighten(theme.palette.secondary.main, 0.85),
              borderColor: lighten(theme.palette.secondary.main, 0.5),
              boxShadow: isHighlighted ? theme.shadows[4] : theme.shadows[1],
              cursor: "pointer",
              ...(showFeedback && {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderBottomColor: "transparent",
              }),
            },
        ]}
      >
        {/* Header: Timestamp and Action Text */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: isUser ? "flex-end" : "space-between",
            mt: theme.spacing(0.5),
          }}
        >
          <Typography
            variant="caption"
            color={lighten(theme.palette.commonground.contrastText, 0.25)}
            sx={{ px: 1.5 }}
          >
            {timeStamp}
          </Typography>
          {interactive && (
            <Typography
              variant="caption"
              color={lighten(theme.palette.commonground.contrastText, 0.25)}
              sx={{ px: 1.5, textTransform: "uppercase" }}
            >
              {isBotAndHasFeedback
                ? isHovered
                  ? "Click to Edit"
                  : `Feedback #${msg.feedbackId} - ${
                      constants.feedbackStatus.find(item => item.value === msg.status)?.label
                    }`
                : isHovered
                  ? "Click to add Feedback"
                  : null}
            </Typography>
          )}
        </Box>

        {/* Message Content Bubble */}
        <Box
          sx={[
            {
              position: "relative",
              userSelect: "none",
              padding: "4px 8px",
              backgroundColor: theme.palette.secondary.main,
              borderRadius: borderRadius,
              color: theme.palette.secondary.contrastText,
              zIndex: 2,
            },
            isUser && {
              alignSelf: "flex-end",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          ]}
        >
          <Typography
            variant="body2"
            sx={[{ padding: "4px 8px", whiteSpace: "pre-wrap" }, isUser && { userSelect: "text" }]}
          >
            {msg.messageText}
          </Typography>
        </Box>

        {/* Floating Feedback Panel (Absolute Positioned) */}
        {!readOnly && (
        <Box
          sx={{
            position: "absolute",
            left: "-1px",
            top: "calc(100% - 1px)",
            width: "calc(100% + 2px)",
            zIndex: 1,
            backgroundColor: theme.palette.secondary.main,
            borderBottomLeftRadius: "16px",
            borderBottomRightRadius: "16px",
            border: `1px solid ${lighten(theme.palette.secondary.main, 0.5)}`,
            borderTop: "none",
            boxShadow: `0px 8px 24px ${alpha(theme.palette.common.black, 0.15)}`,
            opacity: showFeedback ? 1 : 0,
            visibility: showFeedback ? "visible" : "hidden",
            transform: showFeedback ? "translateY(0)" : "translateY(-10px)",
            transition: "opacity 0.2s ease, transform 0.2s ease, visibility 0.2s",
            pointerEvents: showFeedback ? "auto" : "none",
          }}
        >
          <Box
            sx={{
              flexDirection: "column",
              display: "flex",
              backgroundColor: lighten(theme.palette.secondary.main, 0.85),
              color: theme.palette.secondary.main,
              padding: theme.spacing(1.5),
              borderBottomLeftRadius: "15px",
              borderBottomRightRadius: "15px",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ px: 0.5, fontWeight: "bold", fontSize: "0.75rem" }}
            >
              {`FEEDBACK #${msg.feedbackId} - ${
                constants.feedbackStatus.find(item => item.value === msg.status)?.label
              }`}
            </Typography>
            <Typography variant="body2" sx={{ px: 0.5, mt: 0.5 }}>
              {`User Feedback: ${msg.feedback}`}
            </Typography>
          </Box>
        </Box>
        )}
      </Stack>
    </Grid>
  );
});

export default OmniChatBubble;
